import {Dialog} from '@bosch/react-frok';
import React, {useContext, useState} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import {t} from 'i18next';
import {ObjectType} from '../../../models/ObjectType';
import {StorageObject} from '../../../models/StorageObject';
import {
    getLabelEntryMapInitialized,
    getLabelMapKeys,
    getLabelsAsObjectMap,
    getStorageObjectFullyConfigured,
    isValidStorageObjectCommons,
    setLabelEntryMap
} from '../../../utils/StorageObjectUtils';
import {Label} from '../../../models/Label';
import {getEmptyStorageObjectDTO} from '../../../utils/DtoUtils';
import EventEmitter from 'events';
import {
    FolderMetaDataDialog_changeComment,
    FolderMetaDataDialog_changeDateValidFrom,
    FolderMetaDataDialog_changeDateValidTo,
    FolderMetaDataDialog_changeLabels,
    FolderMetaDataDialog_changeLocales,
    FolderMetaDataDialog_changeOwnerRoles,
    FolderMetaDataDialog_changeRoles,
    FolderMetaDataDialog_changeTags,
    FolderMetaDataDialog_changeVersion,
    FolderMetaDataTabLocales_changeLocales,
    FolderMetaDataTabMain_changeLabels
} from '../../../init/events';
import {getDateStart, getEffectiveDate, getFontSize, isNullOrEmpty} from '../../../utils';
import {Valid} from '../../validation/Valid';
import {FolderMetaDataTabView} from './FolderMetaDataTabView';
import {
    ActionButton,
    ContentWrapper,
    DialogEditActionsWrapper,
    HeaderWarning,
    HeaderWarningDetails,
    TextWrapper
} from './FolderMetaDataDialog.style';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {getApplicableTemplateMetadataPropertySettingsMap} from '../../../utils/TemplateUtils';
import {hasWriteOwnerAccess} from '../../../utils/UserUtils';
import {AuthContext} from '../../../context/AuthContext';
import {FolderDialogProps} from '../StorageObjectDialogProps';

const isValidStorageObject = (storageObject: StorageObject, templatePropertySettings: Map<string, TemplatePropertySettings>): boolean => {
    return (isValidStorageObjectCommons(storageObject, templatePropertySettings)) as boolean;
};

/**
 * @name FolderMetaDataDialog
 * @description
 * The dialog to add the metadata of a newly created folder or edited folder - Metadata Tabs
 */
export const FolderMetaDataDialog: React.VFC<FolderDialogProps> = (props) => {
    const {isAuthenticated, roles} = useContext(AuthContext);
    const [templatePropertySettings] = useState<Map<string, TemplatePropertySettings>>(getApplicableTemplateMetadataPropertySettingsMap(ObjectType.FOLDER, props.item?.templateId));
    const [storageObjectInput, setStorageObjectInput] = useState<StorageObject>(getStorageObjectFullyConfigured(props, ObjectType.FOLDER, templatePropertySettings));
    const [isValid, setIsValid] = useState<boolean>(isValidStorageObject(storageObjectInput, templatePropertySettings));
    const [isAuthorizedWriteOwnerAccess, setIsAuthorizedWriteOwnerAccess] = useState<boolean>(hasWriteOwnerAccess(isAuthenticated, roles, storageObjectInput.metadata.ownerRoles, storageObjectInput.metadata.parentOwnerRoles));
    //initial state of objects that have to be reverted on reset event
    const [labelsInitialState] = useState<Map<string, Label>>(cloneDeep(getLabelEntryMapInitialized(props.defaultLocale, storageObjectInput.name, storageObjectInput.labels)));
    const [versionInitialState] = useState(cloneDeep(storageObjectInput.metadata.version ?? ''));
    const eventEmitter = new EventEmitter();
    eventEmitter.setMaxListeners(100);

    const handleChangeLabels = (labels: Map<string, Label>) => {
        let storageObject = storageObjectInput;
        storageObject.labels = labels;
        setStorageObjectInput(storageObject);
        let locales = getLabelMapKeys(props.defaultLocale, labels);
        eventEmitter.emit(FolderMetaDataTabMain_changeLabels, labels);
        eventEmitter.emit(FolderMetaDataTabLocales_changeLocales, locales);
        setIsValid(isValidStorageObject(storageObject, templatePropertySettings));
    };

    const handleLocales = (locales: string[]) => {
        let labels = setLabelEntryMap(locales, storageObjectInput.labels);
        handleChangeLabels(labels);
    };

    const handleChangeVersion = (version: string) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.version = version.trim();
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObject, templatePropertySettings));
    };

    const handleCommentInput = (comment: string) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.comment = comment.trim();
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObject, templatePropertySettings));
    };

    const handleDateValidFrom = (dateValidFrom?: Date) => {
        if ((dateValidFrom !== undefined) && dateValidFrom) {
            dateValidFrom = getDateStart(dateValidFrom);
        } else {
            dateValidFrom = getEffectiveDate();
        }

        let storageObject = storageObjectInput;
        storageObject.metadata.dateValidFrom = dateValidFrom?.getTime();
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObject, templatePropertySettings));
    };

    const handleDateValidTo = (dateValidTo?: Date) => {
        if ((dateValidTo !== undefined) && dateValidTo) {
            dateValidTo = getDateStart(dateValidTo);
        } else {
            dateValidTo = undefined;
        }

        let storageObject = storageObjectInput;
        storageObject.metadata.dateValidTo = dateValidTo?.getTime();
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObject, templatePropertySettings));
    };

    const handleTags = (tags: string[]) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.tags = tags;
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObject, templatePropertySettings));
    };

    const handleRoles = (userRoles: string[]) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.roles = userRoles;
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObject, templatePropertySettings));
    };

    const handleOwnerRoles = (ownerRoles: string[]) => {
        let storageObject = storageObjectInput;
        storageObject.metadata.ownerRoles = ownerRoles;
        setStorageObjectInput(storageObject);
        setIsValid(isValidStorageObject(storageObject, templatePropertySettings));
        setIsAuthorizedWriteOwnerAccess(hasWriteOwnerAccess(isAuthenticated, roles, storageObject.metadata.ownerRoles, storageObjectInput.metadata.parentOwnerRoles));
    };

    const handleSave = (addMore: boolean) => {
        if (!isValid) {
            return;
        }

        let storageObjectDTO = getEmptyStorageObjectDTO();
        let storageObject = cloneDeep(storageObjectInput);
        storageObject.labels = getLabelsAsObjectMap(storageObjectInput.labels);
        storageObjectDTO.item = storageObject;
        props.onSave(storageObjectDTO, addMore);
    };

    eventEmitter.on(FolderMetaDataDialog_changeLabels, handleChangeLabels);
    eventEmitter.on(FolderMetaDataDialog_changeLocales, handleLocales);
    eventEmitter.on(FolderMetaDataDialog_changeVersion, handleChangeVersion);
    eventEmitter.on(FolderMetaDataDialog_changeComment, handleCommentInput);
    eventEmitter.on(FolderMetaDataDialog_changeDateValidFrom, handleDateValidFrom);
    eventEmitter.on(FolderMetaDataDialog_changeDateValidTo, handleDateValidTo);
    eventEmitter.on(FolderMetaDataDialog_changeTags, handleTags);
    eventEmitter.on(FolderMetaDataDialog_changeRoles, handleRoles);
    eventEmitter.on(FolderMetaDataDialog_changeOwnerRoles, handleOwnerRoles);

    let fontSize = getFontSize([
        t('GENERAL_CANCEL_LABEL'),
        t('GENERAL_SAVE_EXIT_LABEL'),
        t('GENERAL_SAVE_ADD_NEW_LABEL')
    ], 10);

    const actionButtons = () => {
        return (
            <DialogEditActionsWrapper id={generateIdentifier()} className='m-dialog__actions'>
                <ActionButton id={generateIdentifier()}
                              key='cancel'
                              mode='secondary'
                              type='button'
                              title={t('GENERAL_CANCEL_LABEL')}
                              label={t('GENERAL_CANCEL_LABEL')}
                              fontSize={fontSize}
                              onClick={props.onClose}/>
                <ActionButton id={generateIdentifier()}
                              key='saveExit'
                              icon='upload'
                              mode='secondary'
                              type='button'
                              label={t('GENERAL_SAVE_EXIT_LABEL')}
                              title={t('GENERAL_SAVE_EXIT_LABEL')}
                              fontSize={fontSize}
                              disabled={((!isValid) || (!isAuthorizedWriteOwnerAccess))}
                              onClick={() => handleSave(false)}/>
                <ActionButton id={generateIdentifier()}
                              key='saveAddNew'
                              icon='upload'
                              mode='primary'
                              type='button'
                              label={t('GENERAL_SAVE_ADD_NEW_LABEL')}
                              title={t('GENERAL_SAVE_ADD_NEW_LABEL')}
                              fontSize={fontSize}
                              disabled={((!isValid) || (!isAuthorizedWriteOwnerAccess))}
                              onClick={() => handleSave(true)}/>
            </DialogEditActionsWrapper>
        );
    };

    return (
        <Dialog id={generateIdentifier()}
                modal
                open
                title={(!isNullOrEmpty(storageObjectInput.id)) ? t('CREATE_EDIT_FOLDER_DIALOG_TITLE_EDIT') : t('CREATE_EDIT_FOLDER_DIALOG_TITLE_CREATE')}
                onClose={props.onClose}>
            {storageObjectInput.toRemove &&
                <div id={generateIdentifier()}>
                    <HeaderWarning id={generateIdentifier()}>
                        {t('CREATE_EDIT_FOLDER_DIALOG_EDIT_EXISTING_ITEM')}
                    </HeaderWarning>
                    <HeaderWarningDetails id={generateIdentifier()}>
                        {t('CREATE_EDIT_FOLDER_DIALOG_EDIT_MARKED_FOR_DELETION_ITEM')}
                    </HeaderWarningDetails>
                </div>
            }
            <ContentWrapper id={generateIdentifier()}>
                <Valid isValid={isValid}
                       message={t('META_DATA_DIALOG_NOT_VALID_CONSTRAINT')}>
                    <FolderMetaDataTabView templatePropertySettings={templatePropertySettings}
                                           item={storageObjectInput}
                                           breadcrumbs={props.breadcrumbs}
                                           currentPath={props.currentPath}
                                           parentOwnerRoles={props.parentOwnerRoles}
                                           parentRoles={props.parentRoles}
                                           parentLocales={props.parentLocales}
                                           language={props.language}
                                           defaultLocale={props.defaultLocale}
                                           rolesDataSource={props.rolesDataSource}
                                           labelsInitialState={labelsInitialState}
                                           versionInitialState={versionInitialState}
                                           eventEmitter={eventEmitter}/>
                </Valid>
            </ContentWrapper>
            <TextWrapper id={generateIdentifier()}>
                {`* ${t('GENERAL_MANDATORY_FIELDS_INFO_TEXT')}`}
            </TextWrapper>
            {actionButtons()}
        </Dialog>
    );
}